import React, { useEffect, useState } from "react";
import { Button, TextField, IconButton, InputAdornment, Typography } from "@mui/material";
import { KeyboardBackspace, Visibility, VisibilityOff } from "@mui/icons-material";
import notificationStore from "../stores/Notification/notificationStore";
import { UserContext } from "../stores/User/User.provider";
import useStore from "../helpers/useStore";
import { observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router-dom";
import { RequestResetEmailForm } from "./ResetComponents/RequestResetEmailForm";
import { ResetPasswordForm } from "./ResetComponents/ResetPasswordForm";
import { UserRegisterDto } from "../api-client";
import { jwtDecode } from "jwt-decode";

const ResetLoginDetails = observer(
  ({ forgotPassword, userEmail }: { forgotPassword?: boolean; userEmail?: string }) => {
    const UserStore = useStore(UserContext);
    const navigate = useNavigate(); // Hook to access the navigate function
    const { changeUserLoginDetails, user, sendOtp } = UserStore;
    const [email, setEmail] = useState(userEmail ? userEmail : "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [mailSent, setMailSent] = useState(false);
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");
    const isResetPasswordPage = location.pathname === "/reset-password";

    useEffect(() => {
      const token = searchParams.get("token");
      const userEmailParam = searchParams.get("user");
      const id = searchParams.get("id");

      if (token) {
        UserStore.verifyShortToken(token)
          .then((result) => {
            if (result && typeof result === "object" && result.valid !== false) {
              if (result.email) {
                setEmail(result.email);
              } else {
                setEmail("");
              }
              setIsOtpVerified(true);
            } else {
              setIsOtpVerified(false);
              setEmail("");
              notificationStore.showMessage("Ungültiger oder abgelaufener Link", "error");
            }
          })
          .catch(() => {
            setIsOtpVerified(false);
            setEmail("");
            notificationStore.showMessage("Ungültiger oder abgelaufener Link", "error");
          });
      } else if (userEmailParam) {
        setEmail(userEmailParam);
        setIsOtpVerified(true);
      }
    }, [location.search]);

    const handleSendOTP = async (e: any) => {
      const sent = await sendOtp(email);
      setMailSent(!!sent);
    };

    const handleResetLoginDetails = async () => {
      try {
        const token = searchParams.get("token");
        let decodedToken;
        if (token) {
          decodedToken = jwtDecode(token);
        }
        const userId = decodedToken?.sub;

        const payload: Partial<UserRegisterDto> = { password };
        // console.log("Payload before setting email or ID:", payload);
        // console.log("User :", user);
        if (email) {
          payload.email = email;
        } else if (userId) {
          payload.id = userId;
        } else {
          // console.log("Final payload  before throwing error:", payload);
          throw new Error("Cannot reset password: no email or user ID available");
        }

        await changeUserLoginDetails(payload as UserRegisterDto, token);

        // Reset form fields
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        navigate("/login");
      } catch (error: any) {
        // Avoid logging full error: axios errors include error.config.data with credentials.
        console.error("Error resetting password:", error?.response?.status, error?.message);
        notificationStore.showMessage("An error occurred while resetting password", "error");
      }
    };

    const handlePasswordChange = (e: any) => {
      setPassword(e.target.value);
    };

    const handleConfirmPasswordChange = (e: any) => {
      setConfirmPassword(e.target.value);
    };

    // Define inputProps object for TextField input element styles
    const confirmPassInputProps = {
      style: {
        borderColor: password === confirmPassword ? "green" : "red",
      },
    };

    return (
      <div>
        {forgotPassword ? (
          <>
            <RequestResetEmailForm
              userEmail={email}
              setEmail={setEmail}
              mailSent={mailSent}
              isOtpVerified={isOtpVerified}
              handleSendOTP={handleSendOTP}
              isResetPasswordPage={isResetPasswordPage}
            />

            {/* Render new password fields only when OTP is verified */}
            <ResetPasswordForm
              isOtpVerified={isOtpVerified}
              password={password}
              confirmPassword={confirmPassword}
              setPassword={setPassword}
              setConfirmPassword={setConfirmPassword}
              handleResetLoginDetails={handleResetLoginDetails}
              id={id}
            />
          </>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Kennwort ändern
            </Typography>
            <TextField
              label="Altes Passwort"
              type="password"
              fullWidth
              value={user?.password || ""}
            />
            <TextField
              label="Neues Kennwort"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={password}
              onChange={handlePasswordChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Bestätige das Passwort"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              InputLabelProps={confirmPassInputProps} // Apply labelProps directly
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button variant="contained" color="primary" onClick={handleResetLoginDetails}>
              Passwort zurücksetzen
            </Button>

            <Typography variant="h6" gutterBottom>
              Ändern Sie die primäre E-Mail-Adresse/den primären Benutzernamen
            </Typography>
            <TextField label="Alte E-Mail-Adresse" type="email" fullWidth value={user?.email} />
            <TextField
              label="Neue Email Adresse"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button variant="contained" color="primary" onClick={handleResetLoginDetails}>
              E-Mail-Adresse zurücksetzen
            </Button>
          </>
        )}
      </div>
    );
  }
);

export default ResetLoginDetails;
